import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2, ScanLine } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp';
const MAX_BYTES = 10 * 1024 * 1024;

/**
 * Scan & auto-fill button for the Add/Edit Traveller form.
 *
 * - Lets user pick a passport (or other doc) image/PDF
 * - Calls /api/documents/ocr-extract (no DB save, no UCC required)
 * - Invokes onExtract(extractedJSON, docType) so the parent can map fields
 *   into form state (e.g. fullName, dateOfBirth, passportNumber, etc.)
 *
 * Designed to work BEFORE the traveller is saved — that's the key
 * difference vs the existing TravellerDocuments component which uses
 * /api/clients/{ucc}/documents/upload.
 */
export default function PassportOCRButton({
  onExtract,
  docType = 'passport',
  label,
  size = 'sm',
  variant = 'default',
  className = '',
  testId = 'scan-passport-btn',
}) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handlePick = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error('File too large (max 10 MB)');
      e.target.value = '';
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', docType);
      const r = await axios.post(`${BACKEND_URL}/api/documents/ocr-extract`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      const extracted = r.data?.extracted || {};
      const filledCount = Object.values(extracted).filter((v) => v && String(v).trim()).length;
      if (filledCount === 0) {
        toast.warning('No readable fields — try a clearer photo');
      } else {
        toast.success(`${filledCount} fields extracted from ${docType}`);
      }
      if (onExtract) onExtract(extracted, docType);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'OCR failed';
      toast.error(typeof msg === 'string' ? msg : 'OCR failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const buttonLabel = label || `Scan ${docType === 'passport' ? 'Passport' : docType} & Auto-fill`;

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT}
        onChange={handleFile}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <Button
        type="button"
        size={size}
        variant={variant}
        onClick={handlePick}
        disabled={busy}
        className={`gap-2 ${className}`}
        data-testid={testId}
      >
        {busy
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Extracting details…</>
          : <><ScanLine className="w-4 h-4" /> {buttonLabel}</>}
      </Button>
    </>
  );
}
